import { CancellationToken } from '@cian/http-api';
import { useUpdateEffect } from '@cian/react-utils';

import { equals } from 'ramda';
import { useCallback, useMemo, useRef, useState } from 'react';

import { type TModifier, modifyJsonQuery, useFiltersContext } from '@/packages/Filters';
import { type NonEmptyArray, type TJsonQuery } from '@/packages/JsonQuery';
import { type AsyncStatus, useTypeSafeApiResult } from '@/packages/typesafe-result';

import { prepareSearchMetaData } from '../../../mappers/prepareSearchMeta';
import { fetchGetMetaWithFixedGeo } from '../../../services/fetchGetMetaWithFixedGeo';
import { useJsonQuerySelector } from '../../../store/slices/jsonQuery/selectors';
import { useLocationSelector } from '../../../store/slices/location/selectors';
import { useApplicationContext } from '../../../utils/applicationContext';

type UseOpenableFilter = {
  onChange: (...modifiers: NonEmptyArray<TModifier>) => void;
  onApply: () => void;
  jsonQuery: TJsonQuery;
  count?: number;
  status: AsyncStatus;
};

export function useOpenableFilter(): UseOpenableFilter {
  const ctx = useApplicationContext();
  const originalJsonQuery = useJsonQuerySelector();
  const location = useLocationSelector();
  const { onApplyOpenableFilter, onChange } = useFiltersContext();

  const [localJsonQuery, setLocalJsonQuery] = useState<TJsonQuery>(originalJsonQuery.current);
  const [appliedModifiers, setAppliedModifiers] = useState<NonEmptyArray<TModifier> | null>(null);

  const cancellationTokenRef = useRef<CancellationToken>(new CancellationToken());
  const { data, safeFetch: fetchMeta, status } = useTypeSafeApiResult(fetchGetMetaWithFixedGeo);

  const handleChange = useCallback(
    (...modifiers: NonEmptyArray<TModifier>) => {
      const nextJsonQuery = modifyJsonQuery(localJsonQuery, modifiers);
      setAppliedModifiers(prevModifiers => (prevModifiers?.concat(modifiers) ?? modifiers) as NonEmptyArray<TModifier>);
      if (!equals(nextJsonQuery, localJsonQuery)) {
        setLocalJsonQuery(nextJsonQuery);
      }
    },
    [localJsonQuery],
  );

  const handleApply = useCallback(() => {
    if (!appliedModifiers) {
      return;
    }
    const meta = data ? prepareSearchMetaData(data) : undefined;
    onChange({ action: 'setJsonQuery', arguments: [localJsonQuery] });
    onApplyOpenableFilter({ modifiers: appliedModifiers, meta });
    setAppliedModifiers(null);
  }, [appliedModifiers, data, localJsonQuery, onApplyOpenableFilter, onChange]);

  useUpdateEffect(() => {
    cancellationTokenRef.current.cancel();
    cancellationTokenRef.current = new CancellationToken();

    fetchMeta(
      ctx,
      { jsonQuery: localJsonQuery, location: location.data },
      {
        cancellationToken: cancellationTokenRef.current,
      },
    );
  }, [localJsonQuery]);

  return useMemo(
    () => ({
      onChange: handleChange,
      onApply: handleApply,
      jsonQuery: localJsonQuery,
      count: data?.data.count,
      status,
    }),
    [data?.data.count, handleApply, handleChange, localJsonQuery, status],
  );
}
